import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { registrarEvento } from "@/pages/ComecarPage";

// Volta do pagamento: é aqui que a conta nasce.
//
// Esta é uma das duas vias — a outra é o webhook do Stripe. Se o webhook
// chegar antes, esta tela encontra a conta pronta e apenas entra nela; se o
// webhook falhar, esta tela resolve sozinha. Um pagamento confirmado nunca
// pode terminar sem conta.

export const TENTATIVAS = 4;
export const ESPERA_MS = 2500;

export default function ConfirmandoCadastroPage() {
  const [params] = useSearchParams();
  const { confirmarCadastro } = useAuth();
  const navigate = useNavigate();
  const [erro, setErro] = useState("");
  const jaTentou = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setErro("Não encontramos a referência do pagamento.");
      return;
    }
    if (jaTentou.current) return;
    jaTentou.current = true;

    let ativo = true;
    (async () => {
      // O Stripe pode levar um instante para marcar a sessão como paga;
      // insistir algumas vezes evita mandar embora quem já pagou.
      for (let tentativa = 1; tentativa <= TENTATIVAS && ativo; tentativa += 1) {
        try {
          await confirmarCadastro(sessionId);
          // A conversão é aqui: antes do pagamento existia só intenção, e
          // contar o checkout como cadastro inflaria o número no Meta e no
          // Google com gente que desistiu na tela do cartão.
          registrarEvento("lp_cadastro_concluido", { etapa: "pagamento_confirmado" });
          // Sem condição: o efeito duplo do StrictMode cancela a primeira
          // execução, e quem pagou ficaria parado nesta tela para sempre.
          navigate("/dashboard");
          return;
        } catch (falha) {
          const status = falha?.response?.status;
          if (status && status !== 402 && status !== 409) {
            if (ativo) setErro(falha?.response?.data?.detail || "Não foi possível concluir o cadastro.");
            return;
          }
          if (tentativa === TENTATIVAS) {
            if (ativo) {
              setErro(
                "O pagamento ainda não foi confirmado pelo banco. Se o valor já saiu, " +
                "sua conta é criada automaticamente em instantes — tente entrar daqui a pouco.",
              );
            }
            return;
          }
          await new Promise((r) => setTimeout(r, ESPERA_MS));
        }
      }
    })();

    return () => { ativo = false; };
  }, [params, confirmarCadastro, navigate]);

  return (
    <div className="min-h-screen bg-[#F4F7FB] flex items-center justify-center px-6">
      <div className="bg-white border border-[#D1DEEE] rounded-lg p-8 max-w-md w-full text-center">
        {erro ? (
          <>
            <AlertCircle size={28} strokeWidth={1.5} className="text-[#003882] mx-auto mb-4" aria-hidden="true" />
            <h1 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Quase lá
            </h1>
            <p className="text-[#335480] mt-2" data-testid="confirmacao-erro">{erro}</p>
            <Link
              to="/login"
              className="inline-block mt-6 bg-[#003882] hover:bg-[#00285e] text-white font-semibold rounded-md px-5 py-2.5"
            >
              Ir para o login
            </Link>
          </>
        ) : (
          <>
            <Loader2 size={28} className="animate-spin text-[#003882] mx-auto mb-4" aria-hidden="true" />
            <h1 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Confirmando seu pagamento
            </h1>
            <p className="text-[#335480] mt-2" data-testid="confirmacao-andamento">
              Estamos criando sua conta. Isso leva alguns segundos — não feche esta página.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
